import type { FileRouteTypes } from '@client/routeTree.gen';

/**
 * Extracts parameter types from a route pattern.
 *
 * @example
 * ```typescript
 * type Params1 = ExtractRouteParams<'/console/$orgId'>;
 * // Result: { orgId: string }
 *
 * type Params2 = ExtractRouteParams<'/admin-console/organisation-management/organisations/$orgId'>;
 * // Result: { orgId: string }
 *
 * type Params3 = ExtractRouteParams<'/admin-console'>;
 * // Result: {}
 * ```
 */
export type ExtractRouteParams<T extends string> = string extends T
    ? Record<string, string> // Fallback for generic strings
    : T extends `${string}/$${infer Param}/${infer Rest}`
      ? { [K in Param]: string } & ExtractRouteParams<`/${Rest}`>
      : T extends `${string}/$${infer Param}`
        ? { [K in Param]: string }
        : Record<string, never>; // No parameters - empty object type

/**
 * Extracts route parameters from a pathname by matching it against a route pattern with full type safety.
 * This utility replaces the non-existent `matchByPath` function that was mistakenly imported during
 * the migration from React Router DOM to TanStack Router.
 *
 * This implementation manually parses path patterns and extracts parameters without relying on
 * the router's current location, which is necessary for authentication redirects where we need
 * to parse paths that haven't been navigated to yet.
 *
 * **Type Safety Features:**
 * - `pattern` parameter is constrained to valid routes from your route tree
 * - Return type is automatically inferred from the pattern (no manual type annotation needed)
 * - TypeScript will error if you use an invalid route pattern
 *
 * @param pathname - The path to match (e.g., '/console/123/claims')
 * @param pattern - A valid route pattern from your route tree (e.g., '/console/$orgId')
 * @param options - Optional matching options
 * @param options.fuzzy - If true, allows partial matches (e.g., '/console/$orgId' matches '/console/123/claims')
 * @returns The extracted parameters object with inferred types, or undefined if no match
 *
 * @example
 * ```typescript
 * // ✅ Automatic type inference - no type annotation needed!
 * const params = extractPathParams('/console/123', '/console/$orgId');
 * // params: { orgId: string } | undefined
 * if (params) {
 *   console.log(params.orgId); // ✅ TypeScript knows orgId exists and is a string
 * }
 *
 * // ✅ Fuzzy match (pattern can be a prefix of the path)
 * const fuzzyParams = extractPathParams('/console/123/claims', '/console/$orgId', { fuzzy: true });
 * // fuzzyParams: { orgId: string } | undefined
 *
 * // ✅ Routes without parameters
 * const noParams = extractPathParams('/admin-console', '/admin-console');
 * // noParams: Record<string, never> | undefined
 *
 * // ❌ TypeScript error: invalid route pattern!
 * const invalid = extractPathParams('/console/123', '/console-new/$orgId');
 * //                                                 ^^^^^^^^^^^^^^^^^^^
 * // Error: '/console-new/$orgId' is not assignable to valid route paths
 *
 * // ✅ Multiple parameters (from admin console routes)
 * const multiParams = extractPathParams(
 *   '/admin-console/organisation-management/organisations/123',
 *   '/admin-console/organisation-management/organisations/$orgId'
 * );
 * // multiParams: { orgId: string } | undefined
 * ```
 */
export function extractPathParams<TPath extends FileRouteTypes['fullPaths']>(
    pathname: string,
    pattern: TPath,
    options?: { fuzzy?: boolean },
): ExtractRouteParams<TPath> | undefined {
    // Normalize paths by removing trailing slashes and ensuring they start with /
    const normalizePath = (path: string) => {
        const normalized = path.startsWith('/') ? path : `/${path}`;
        return normalized.endsWith('/') && normalized.length > 1 ? normalized.slice(0, -1) : normalized;
    };

    const normalizedPathname = normalizePath(pathname);
    const normalizedPattern = normalizePath(pattern);

    // Split paths into segments
    const pathnameSegments = normalizedPathname.split('/').filter(Boolean);
    const patternSegments = normalizedPattern.split('/').filter(Boolean);

    // Extract parameter names and their positions from the pattern
    const params: Record<string, string> = {};

    for (let i = 0; i < patternSegments.length; i++) {
        const patternSegment = patternSegments[i];
        const pathnameSegment = pathnameSegments[i];

        // If we've run out of pathname segments but pattern has more
        if (pathnameSegment === undefined) {
            return undefined;
        }

        // Check if this segment is a parameter (starts with $)
        if (patternSegment.startsWith('$')) {
            const paramName = patternSegment.slice(1); // Remove the $
            params[paramName] = pathnameSegment;
        } else if (patternSegment === pathnameSegment) {
        } else {
            // Segments don't match
            return undefined;
        }
    }

    // If fuzzy is false, pathname must have the exact same number of segments as pattern
    if (!options?.fuzzy && pathnameSegments.length !== patternSegments.length) {
        return undefined;
    }

    // If fuzzy is true, we allow pathname to have more segments than pattern
    // (the pattern is a prefix of the pathname)

    return params as ExtractRouteParams<TPath>;
}
